import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ClusterOption {
  id: string;
  name: string;
  color: string;
}

export interface ContratoOption {
  id: string;
  nome: string;
  numero_contrato: string;
  status: string;
}

/**
 * Fetches clusters and contracts available to the current user for portfolio reports.
 * Contract list is automatically filtered by RLS — only accessible contracts are returned.
 */
export function usePortfolioContracts(selectedClusterId: string | null) {
  const { isAdmin, isCoAdmin, role } = useAuth();
  const isGestorOrAbove = isAdmin || isCoAdmin || role === 'gestor';

  // ── Clusters (todos acessíveis via RLS)
  const { data: clusters = [], isLoading: clustersLoading } = useQuery({
    queryKey: ['portfolio-clusters'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clusters')
        .select('id, name, color')
        .order('name');
      if (error) throw error;
      return (data ?? []) as ClusterOption[];
    },
    enabled: isGestorOrAbove,
    staleTime: 5 * 60 * 1000,
  });

  // ── Contratos do cluster selecionado (RLS filtra automaticamente)
  const { data: contratos = [], isLoading: contratosLoading } = useQuery({
    queryKey: ['portfolio-contratos', selectedClusterId],
    queryFn: async () => {
      if (!selectedClusterId) return [];
      const { data, error } = await supabase
        .from('emp_contratos')
        .select('id, nome, numero_contrato, status')
        .eq('cluster_id', selectedClusterId)
        .eq('ativo', true)
        .order('nome');
      if (error) throw error;
      return (data ?? []) as ContratoOption[];
    },
    enabled: !!selectedClusterId && isGestorOrAbove,
    staleTime: 2 * 60 * 1000,
  });

  return {
    clusters,
    contratos,
    clustersLoading,
    contratosLoading,
    isGestorOrAbove,
  };
}
